import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import {
  Box,
  IconButton,
  Button,
  Card,
  Grid,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { grayBorderedButton } from '../../../../../assets/styles/grayBorderedButton.style';
import DepartmentObject from '../../../../../interfaces/departmentObject';
import HeaderTextComponent from '../../../../common/headerTextComponent';
import { classes } from '../buttonComponent.classes';

interface CustomDepartmentFormProps {
  onChangeDepartment: (input: number | null, action: string) => void;
  list: Array<DepartmentObject>;
  customDepartmentToAdd: string;
  onChangeCustomDepartmentToAdd: (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  onAddCustomDepartment: () => void;
}
const CustomDepartmentForm = ({
  onChangeDepartment,
  list,
  customDepartmentToAdd,
  onChangeCustomDepartmentToAdd,
  onAddCustomDepartment,
}: CustomDepartmentFormProps) => {
  const DEPARTMENT_BUTTON_TYPE = 'department';

  const [addCustomDepartmentField, setAddCustomDepartmentField] =
    useState(false);
  const intl = useIntl();

  const customDepartmentClickHandler = () => {
    onAddCustomDepartment();
  };

  const enableCustomDepartmentClickHandler = () => {
    setAddCustomDepartmentField(true);
  };

  const disableCustomDepartmentClickHandler = () => {
    setAddCustomDepartmentField(false);
  };

  const getDepartmentType = (type: string) => {
    if (type === 'Mandatory' || type === 'Default') {
      return ` (${type})`;
    } else {
      return '';
    }
  };

  const [selectedData, setSelectedData] = useState<DepartmentObject | null>(null);
  const [anchorOption, setAnchorOption] = useState<null | (EventTarget & HTMLButtonElement)>(null);

  const handleCellClick = (
    event: React.MouseEvent<HTMLButtonElement | null>,
    rowData: DepartmentObject,
  ) => {
    setSelectedData(rowData);
    setAnchorOption(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorOption(null);
  };

  const handleChange = (option: string) => {
    if (selectedData !== null) {
      onChangeDepartment(selectedData.id, option);
    }
    handleClosePopover();
  };

  const getTurnOnTurnOffStatus = () => {
    if (selectedData !== null) {
      return selectedData.is_active === true
        ? intl.formatMessage({ defaultMessage: 'Turn Off' })
        : intl.formatMessage({ defaultMessage: 'Turn On' });
    } else {
      return '';
    }
  };

  return (
    <>
      <Stack
        sx={{ mb: 2 }}
        direction='row'
        alignItems='center'
        justifyContent='space-between'
      >
        <HeaderTextComponent variant='h1'>
          <FormattedMessage defaultMessage='Departments' />
        </HeaderTextComponent>
        {addCustomDepartmentField === false && (
          <Button
            sx={{
              ...classes.button[DEPARTMENT_BUTTON_TYPE],
              ...{
                textTransform: 'capitalize',
              },
              ...{
                height: '100%',
              },
              ...grayBorderedButton,
              '& .MuiButton-startIcon': {
                marginLeft: 0,
                marginRight: '-6px',
              },
            }}
            startIcon={<AddIcon />}
            onClick={enableCustomDepartmentClickHandler}
          >
            <FormattedMessage defaultMessage='Department' />
          </Button>
        )}
        {addCustomDepartmentField === true && (
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={2}
          >
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-end'
              spacing={1}
            >
              <Typography
                variant='body2'
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '16px',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0em',
                  textAlign: 'left',
                  color: '#878787',
                }}
              >
                <FormattedMessage defaultMessage='Enter department you wish to add:' />
              </Typography>
              <TextField
                margin='none'
                variant='standard'
                placeholder='Finance'
                value={customDepartmentToAdd}
                onChange={onChangeCustomDepartmentToAdd}
                InputProps={{ disableUnderline: true }}
                sx={{
                  '& .MuiInputBase-input': {
                    width: '180px',
                    height: '23px',
                    border: '1px solid #00000080',
                    borderRadius: '6px',
                    padding: '12px 12px 12px 12px',
                    fontFamily: 'Schibsted Grotesk',
                    fontSize: '16px',
                    fontWeight: 400,
                    lineHeight: '20px',
                    letterSpacing: '0em',
                    textAlign: 'left',
                    color: '#878787',
                  },
                  '& .MuiFormLabel-root': {
                    top: '8px',
                    left: '12px',
                  },
                }}
              />
            </Stack>
            <Stack
              direction='row'
              alignItems='center'
              justifyContent='flex-end'
              spacing={1}
            >
              <IconButton
                size='small'
                sx={{
                  width: '28px',
                  height: '28px',
                  flexShrink: 0,
                  padding: 0, // Remove default padding
                  borderColor: '#33DA7F',
                  borderRadius: '5px',
                  borderWidth: '3px',
                  borderStyle: 'solid',
                  backgroundColor: '#33DA7F14',
                  '& :hover': {
                    borderRadius: '5px',
                  },
                }}
                onClick={customDepartmentClickHandler}
              >
                <CheckIcon
                  fontSize='small'
                  sx={{
                    color: '#33DA7F',
                  }}
                />
              </IconButton>
              <IconButton
                size='small'
                sx={{
                  width: '28px',
                  height: '28px',
                  flexShrink: 0,
                  padding: 0, // Remove default padding
                  borderColor: '#FF5E56',
                  borderRadius: '5px',
                  borderWidth: '3px',
                  borderStyle: 'solid',
                  backgroundColor: '#FF5E5614',
                  '& :hover': {
                    borderRadius: '5px',
                  },
                }}
                onClick={disableCustomDepartmentClickHandler}
              >
                <CloseIcon
                  fontSize='small'
                  sx={{
                    color: '#FF5E56',
                  }}
                />
              </IconButton>
            </Stack>
          </Stack>
        )}
      </Stack>

      <Card
        sx={{
          borderRadius: '6px',
          border: '1px solid #00000029',
          width: '100%',
          opacity: '1',
          pointerEvents: 'auto',
        }}
      >
        <Box
          sx={{
            px: 2.3,
            py: 2,
            backgroundColor: '#EEEEEE',
            borderBottom: '1px solid #CCCCCC',
          }}
        >
          <Grid container spacing={0} columns={{ xs: 4, sm: 8, md: 12 }}>
            <Grid item sm={4}>
              <Typography
                variant='body2'
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  letterSpacing: '0em',
                  textAlign: 'left',
                  color: '#878787',
                }}
              >
                <FormattedMessage defaultMessage='List of Departments' />
              </Typography>
            </Grid>
            <Grid item sm={4}>
              <Typography
                variant='body2'
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  letterSpacing: '0em',
                  textAlign: 'center',
                  color: '#878787',
                }}
              >
                <FormattedMessage defaultMessage='Status' />
              </Typography>
            </Grid>
            <Grid item sm={4}>
              <Typography
                variant='body2'
                sx={{
                  fontFamily: 'Schibsted Grotesk',
                  fontSize: '14px',
                  fontWeight: 400,
                  lineHeight: '18px',
                  letterSpacing: '0em',
                  textAlign: 'right',
                  color: '#878787',
                }}
              >
                <FormattedMessage defaultMessage='Actions' />
              </Typography>
            </Grid>
          </Grid>
        </Box>
        {list.length > 0 &&
          list.map((item, index) => (
            <Box
              key={index}
              sx={{
                px: 2.25,
                pt: 1.5,
                pb: 0.5,
                borderBottom: '1px solid #E7E7E7',
                height: '3rem',
              }}
            >
              {' '}
              <Grid
                container
                direction='row'
                columns={12}
                spacing={2}
                alignItems='center'
                justifyContent='space-between'
              >
                <Grid
                  container
                  direction='row'
                  xs={4}
                  item
                  alignItems='center'
                  justifyContent='flex-start'
                >
                  <Stack
                    direction='row'
                    alignItems='flex-end'
                    justifyContent='flex-start'
                    spacing={1}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'Schibsted Grotesk',
                        fontSize: '16px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0em',
                        textAlign: 'left',
                        color: '#181818',
                      }}
                    >
                      {item.name}
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{
                        fontFamily: 'Schibsted Grotesk',
                        fontSize: '12px',
                        fontWeight: 400,
                        lineHeight: '20px',
                        letterSpacing: '0em',
                        textAlign: 'left',
                        color: '#878787',
                      }}
                    >
                      {getDepartmentType(item.classification ?? '')}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid
                  container
                  direction='row'
                  xs={4}
                  item
                  alignItems='center'
                  justifyContent='center'
                >
                  {item.is_active === true ? (
                    <Box
                      sx={{
                        padding: '2px 6px 2px 6px',
                        borderRadius: '14px',
                        gap: '10px',
                        backgroundColor: '#D2F6E4',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontFamily: 'Schibsted Grotesk',
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '18px',
                          letterSpacing: '0em',
                          textAlign: 'center',
                          color: '#1ED07A',
                        }}
                      >
                        <FormattedMessage defaultMessage='Active' />
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        padding: '2px 6px 2px 6px',
                        borderRadius: '14px',
                        gap: '10px',
                        backgroundColor: '#FFEFD7',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{
                          fontFamily: 'Schibsted Grotesk',
                          fontSize: '14px',
                          fontWeight: 500,
                          lineHeight: '18px',
                          letterSpacing: '0em',
                          textAlign: 'center',
                          color: '#FF9900',
                        }}
                      >
                        <FormattedMessage defaultMessage='Inactive' />
                      </Typography>
                    </Box>
                  )}
                </Grid>
                <Grid
                  container
                  direction='row'
                  xs={4}
                  item
                  alignItems='center'
                  justifyContent='flex-end'
                >
                  <IconButton
                    sx={{ width: '30px', height: '30px' }}
                    size='large'
                    onClick={(event) => handleCellClick(event, item)}
                  >
                    {item.classification !== 'Mandatory' && <MoreHorizIcon />}
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}
      </Card>

      <Popover
        open={Boolean(anchorOption)}
        anchorEl={anchorOption}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 0,
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: -110,
        }}
      >
        {selectedData && (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              maxWidth: '246px',
              maxHeight: '400px',
              overflow: 'auto',
              borderRadius: '6px',
              backgroundColor: '#FFF',
            }}
          >
            {(selectedData.classification === ''
              ? [...[getTurnOnTurnOffStatus()], ...['Remove']]
              : [getTurnOnTurnOffStatus()]
            ).map((item, index) => (
              <Button
                sx={{
                  width: '100%',
                  margin: '0',
                  color: '#000',
                  fontFamily: 'Schibsted Grotesk',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: 'normal',
                  textTransform: 'none',
                  transition: 'background-color 0.3s',
                  p: '10px 28px 10px 28px',
                  textAlign: 'left',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 206, 207, 0.3)',
                  },
                }}
                key={index}
                onClick={() => handleChange(item)}
              >
                {item}
              </Button>
            ))}
          </div>
        )}
      </Popover>
    </>
  );
};

export default CustomDepartmentForm;